import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ApiTemplatesService } from '../data/services/api/api-templates.service';
import { TemplateService } from '../data/services/view/template.service';
import { Location } from '@angular/common';
import { IMasterTemplate } from '../data/interfaces/master-template.interface';

@Component({
  selector: 'app-template-detail',
  templateUrl: './template-detail.component.html',
  styleUrls: ['./template-detail.component.scss'],
  providers: [MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplateDetailComponent implements OnInit {
  template: IMasterTemplate | null = null;
  templateId: string | null = null;
  isLoading = true;
  hasError = false;
  errorMessage = '';

  // Estado de preview
  showEmailPreview = false;
  previewEmailHtml = '';

  // Estado de edición
  showEditDialog = false;

  // Variables de ejemplo para preview (sincronizadas con AVAILABLE_VARIABLES)
  sampleVariables: { [key: string]: string } = {
    // Cliente
    clientName: 'Juan Pérez',
    clientEmail: 'cliente@ejemplo.com',
    clientPhone: '555-1234-5678',
    
    // Arrendamiento
    leaseNumber: 'L-001234',
    
    // Financiero
    amount: '$15,000.00',
    dueDate: '31 de diciembre de 2025',
    daysOverdue: '5 días',
    totalDebt: '$8,500.00',
    
    // Empresa
    companyName: 'Rendafin'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiTemplatesService: ApiTemplatesService,
    private templateService: TemplateService,
    private messageService: MessageService,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.templateId = this.route.snapshot.paramMap.get('id');
    
    if (this.templateId) {
      this.loadTemplate(this.templateId);
    } else {
      this.showError('ID de plantilla no válido');
    }
  }

  /**
   * Carga los datos de la plantilla desde el backend
   */
  loadTemplate(id: string): void {
    this.isLoading = true;
    this.hasError = false;
    this.cdr.markForCheck();

    this.apiTemplatesService.getCollection(id).subscribe({
      next: (response: any) => {
        if (!response.error && response.data) {
          this.template = response.data;
          this.hasError = false;
        } else {
          this.showError(response.msg || 'Error al cargar la plantilla');
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.showError('Error al cargar la plantilla desde el servidor');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Muestra un error al usuario
   */
  showError(message: string): void {
    this.hasError = true;
    this.errorMessage = message;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 5000
    });
  }

  /**
   * Vuelve a la lista de plantillas
   */
  goBack(): void {
    this.location.back();
  }

  /**
   * Abre el modal de edición de la plantilla
   */
  editTemplate(): void {
    if (this.templateId && this.template) {
      // Cargar los datos en el servicio de template
      this.templateService.loadEntityForEdit(this.template);
      this.showEditDialog = true;
      this.cdr.markForCheck();
    }
  }

  /**
   * Oculta el modal de edición
   */
  hideEditDialog(): void {
    this.showEditDialog = false;
    this.cdr.markForCheck();
  }

  /**
   * Se ejecuta cuando se cierra el dialog de edición
   */
  onEditDialogHide(): void {
    // Limpiar el formulario del servicio
    this.templateService.newEntity();
  }

  /**
   * Guarda los cambios de la plantilla editada
   */
  saveEditedTemplate(): void {
    // Validar variables antes de enviar
    if (!this.templateService.validateVariables()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de Validación',
        detail: 'Hay variables usadas en los templates que no están definidas en la lista de variables'
      });
      return;
    }
    
    if (!this.templateService.entity.valid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Incompleto',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    // Limpiar templates opcionales
    const data = this.templateService.cleanTemplateData(this.templateService.entity);
    
    // Actualizar template
    this.apiTemplatesService.patchCollection(
      data, 
      undefined, 
      `${this.apiTemplatesService.path}/${this.templateId}`
    ).subscribe({
      next: (r: any) => {
        if (r?.error) {
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Error', 
            detail: r.msg || 'Error al actualizar la plantilla'
          });
          return;
        }
        
        this.messageService.add({ 
          severity: 'success', 
          summary: 'Éxito', 
          detail: `Plantilla actualizada correctamente` 
        });
        
        // Recargar los datos del template
        if (this.templateId) {
          this.loadTemplate(this.templateId);
        }
        
        this.hideEditDialog();
      },
      error: (error) => {
        console.error('Error updating template:', error);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Error al actualizar la plantilla'
        });
      }
    });
  }

  /**
   * Reemplaza variables en un texto con valores de ejemplo
   * Las variables reconocidas se reemplazan con datos de muestra
   * Las variables no reconocidas se eliminan silenciosamente (templates legacy)
   */
  replaceVariables(text: string): string {
    if (!text) return '';
    
    let result = text;
    
    // Paso 1: Reemplazar variables reconocidas con valores de ejemplo
    Object.keys(this.sampleVariables).forEach(key => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      result = result.replace(regex, this.sampleVariables[key]);
    });
    
    // Paso 2: Eliminar variables no reconocidas (legacy) para evitar mostrar {{variable}}
    result = result.replace(/\{\{(\w+)\}\}/g, '');
    
    return result;
  }

  /**
   * Muestra el preview del email en un modal
   */
  showEmailPreviewModal(): void {
    if (this.template?.emailTemplate?.html) {
      this.previewEmailHtml = this.replaceVariables(this.template.emailTemplate.html);
      this.showEmailPreview = true;
      this.cdr.markForCheck();
    }
  }

  /**
   * Cierra el modal de preview
   */
  closeEmailPreview(): void {
    this.showEmailPreview = false;
    this.cdr.markForCheck();
  }

  /**
   * Obtiene el ícono según el estado activo/inactivo
   */
  getStatusIcon(): string {
    return this.template?.active ? 'pi pi-check-circle' : 'pi pi-times-circle';
  }

  /**
   * Obtiene la clase CSS según el estado activo/inactivo
   */
  getStatusClass(): string {
    return this.template?.active ? 'status-active' : 'status-inactive';
  }

  /**
   * Obtiene el label del estado
   */
  getStatusLabel(): string {
    return this.template?.active ? 'Activa' : 'Inactiva';
  }

  /**
   * Verifica si una plantilla de canal está configurada
   */
  hasEmailTemplate(): boolean {
    return !!this.template?.emailTemplate;
  }

  hasSmsTemplate(): boolean {
    return !!this.template?.smsTemplate;
  }

  hasWhatsappTemplate(): boolean {
    return !!this.template?.whatsappTemplate;
  }

  hasCallScript(): boolean {
    return !!this.template?.callScript;
  }

  hasAutoCallConfig(): boolean {
    return !!this.template?.autoCallConfig;
  }

  /**
   * Cuenta los canales configurados
   */
  getConfiguredChannelsCount(): number {
    let count = 0;
    if (this.hasEmailTemplate()) count++;
    if (this.hasSmsTemplate()) count++;
    if (this.hasWhatsappTemplate()) count++;
    if (this.hasCallScript() || this.hasAutoCallConfig()) count++;
    return count;
  }

  /**
   * Formatea una fecha para mostrarla
   */
  formatDate(date: string | Date | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
